import { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { Table, TBody, TD, TH, THead, TR } from '@/design-system';
import { CARD_TABLE_DENSE } from './DowntimeCard';
import { DOWNTIME_ITEMS, DRAW_START, DRAW_END, type DowntimeItemId } from '../maintenance/downtime.seed';
import {
  STATE_LABELS,
  dayTicks,
  formatEventStart,
  formatEventWindow,
  formatHours,
  itemLabel,
  reasonQueue,
  timelineSegments,
} from '../maintenance/Downtime.transform';

/** Review status reads as a state pill: pending is the open condition,
 *  recoded is information in flight, confirmed is settled and stays neutral. */
const REVIEW_TONE: Record<string, string> = {
  pending: 'bg-[color-mix(in_srgb,var(--state-warning)_11%,transparent)] text-[var(--state-warning)]',
  recoded: 'bg-[color-mix(in_srgb,var(--state-info)_10%,transparent)] text-[var(--state-info)]',
  confirmed: 'bg-[var(--hairline)] text-ink-secondary',
};

/** Colour is the abnormal state (HPHMI): running at design is the bare track,
 *  a planned shutdown is hatched ink, never the brand crimson. */
const STATE_TONE: Record<string, string> = {
  running_design: 'bg-[color-mix(in_srgb,var(--ink-tertiary)_16%,transparent)]',
  running_degraded: 'bg-[var(--state-warning)]',
  idle_upstream: 'bg-[var(--state-info)]',
  idle_downstream: 'bg-[var(--ink-tertiary)]',
  offline_breakdown: 'bg-[var(--state-blocked)]',
  offline_shutdown: 'bg-[repeating-linear-gradient(135deg,var(--ink-secondary)_0_2px,transparent_2px_5px)]',
};

export function DowntimeDrill() {
  const [open, setOpen] = useState(false);
  const ticks = dayTicks();
  const reasonRows = reasonQueue();
  return (
    <details open={open} onToggle={(event) => setOpen(event.currentTarget.open)} className="card-matte-2 rounded-2xl">
      <summary className="flex cursor-pointer list-none items-center gap-2.5 rounded-2xl px-4 py-3 transition-colors duration-150 hover:bg-[var(--surface-1)] [&::-webkit-details-marker]:hidden">
        <ChevronRight aria-hidden="true" className={`h-3.5 w-3.5 shrink-0 text-muted transition-transform duration-200 ${open ? 'rotate-90' : ''}`} />
        <span className="flex items-baseline gap-2.5">
          <span className="text-sm font-semibold text-ink-primary">Event detail</span>
          <span className="text-xs text-muted">{DOWNTIME_ITEMS.length} items · {reasonRows.length} coded events</span>
        </span>
      </summary>
      {open ? (
        <div className="space-y-4 px-4 pb-4">
          <section aria-label="State timeline">
            <h4 className="mb-2 text-sm font-semibold text-ink-primary">State timeline · 01–07 Jun</h4>
            <div className="space-y-2 overflow-x-auto">
              <div className="grid min-w-[760px] grid-cols-[11rem_minmax(0,1fr)] items-center gap-x-3 gap-y-1.5">
                <span />
                <div className="relative h-5">{ticks.map((tick) => <span key={tick.at} className="absolute text-[10px] text-muted" style={{ left: `${tick.leftPct}%` }}>{tick.label}</span>)}</div>
                {DOWNTIME_ITEMS.map((item) => <TimelineRow key={item.id} itemId={item.id} />)}
              </div>
              <div className="flex flex-wrap gap-x-3.5 gap-y-1 pt-2 text-xs text-muted">{Object.entries(STATE_LABELS).map(([state, label]) => <span key={state} className="inline-flex items-center gap-1"><i className={`h-2.5 w-2.5 rounded-[3px] ${STATE_TONE[state]}`} />{label}</span>)}</div>
            </div>
          </section>
          <section aria-label="Reason-code review">
            <h4 className="mb-1.5 text-sm font-semibold text-ink-primary">Reason-code review</h4>
            <div className={CARD_TABLE_DENSE}>
              <Table wrap={false} className="tabular-nums">
                <THead><TR><TH className="min-w-[5rem]">Event</TH><TH className="min-w-[7rem]">Item</TH><TH>Code</TH><TH className="w-[42%]">Description</TH><TH>Category</TH><TH>Status</TH><TH>Entered by</TH></TR></THead>
                <TBody>{reasonRows.map((row) => <TR key={row.id}><TD className="tabular-nums">{formatEventStart(row.start)}</TD><TD>{itemLabel(row.itemId)}</TD><TD className="whitespace-nowrap font-mono text-xs">{row.code}</TD><TD>{row.description}</TD><TD className="whitespace-nowrap text-xs">{row.category}</TD><TD className="whitespace-nowrap"><span className={`inline-flex rounded-full px-2 py-px text-[10px] font-semibold uppercase tracking-[0.05em] ${REVIEW_TONE[row.reviewStatus] ?? REVIEW_TONE.confirmed}`}>{row.reviewStatus}</span></TD><TD className="text-xs">{row.enteredBy}</TD></TR>)}</TBody>
              </Table>
            </div>
          </section>
        </div>
      ) : null}
    </details>
  );
}

function TimelineRow({ itemId }: { itemId: DowntimeItemId }) {
  const segments = timelineSegments(itemId, DRAW_START, DRAW_END);
  return (
    <div className="contents">
      <span className="truncate text-xs font-medium text-ink-primary" title={itemLabel(itemId)}>{itemLabel(itemId)}</span>
      <div className="relative h-4 overflow-hidden rounded bg-[color-mix(in_srgb,var(--ink-tertiary)_16%,transparent)]">
        {segments.map((segment, index) => <span key={`${segment.start}-${index}`} title={`${STATE_LABELS[segment.state]} · ${formatEventWindow(segment.start, segment.end)} · ${formatHours((segment.widthPct / 100) * 168)}`} className={`absolute inset-y-0 ${STATE_TONE[segment.state]}`} style={{ left: `${segment.startPct}%`, width: `${segment.widthPct}%` }} />)}
      </div>
    </div>
  );
}
